'use client'

import type { ActivitiesBlock as ActivitiesBlockProps, Media } from 'src/payload-types'

import React from 'react'
import Link from 'next/link'
import { motion } from 'motion/react'
import { useInView } from 'react-intersection-observer'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { getLink } from '@/utilities/getLink'
import Image from 'next/image'
import { imgproxyEnabled, imgproxyLoader } from '@/utilities/imgproxy'
import AnimatedText from '@/components/Antonie/Components/animated-text'

type Activity = {
  title: string
  description: string
  image: Media | null | string | undefined
  link?: {
    type?: ('reference' | 'custom') | null
    newTab?: boolean | null
    url?: string | null
    label?: string | null
    reference?: unknown
  }
}

type Props = {
  className?: string
} & ActivitiesBlockProps

export const ActivitiesBlock: React.FC<Props> = ({ activities, title }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const titleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { staggerChildren: 0.2 },
    },
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  }

  return (
    <section className="bg-stone-100 py-12 md:py-24">
      <div className="container mx-auto px-6">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={titleVariants}
          className="mb-4 text-center md:mb-16"
        >
          <AnimatedText
            text={title}
            type="h2"
            variant="simple"
            classNameText="text-2xl md:text-4xl"
            className=" font-heading text-2xl font-medium text-gray-900 md:text-4xl"
            delay={0.2}
          />
        </motion.div>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6"
        >
          {activities.map((activity: Activity, index: number) => {
            const content = (
              <motion.div
                variants={itemVariants}
                className="group relative aspect-[4/3] overflow-hidden md:aspect-[3/2]"
              >
                <Image
                  loader={imgproxyEnabled ? imgproxyLoader : undefined}
                  src={getMediaUrl(activity.image)}
                  alt={activity.title}
                  width={800}
                  height={600}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-heading text-lg font-medium uppercase tracking-widest text-white md:text-xl">
                    {activity.title}
                  </h3>
                </div>
              </motion.div>
            )

            const hasLink = activity.link && (activity.link.url || activity.link.reference)
            if (hasLink) {
              return (
                <Link
                  key={index}
                  href={getLink(activity.link)}
                  target={activity.link?.newTab ? '_blank' : '_self'}
                  aria-label={activity.title}
                  className="block"
                >
                  {content}
                </Link>
              )
            }

            return <div key={index}>{content}</div>
          })}
        </motion.div>
      </div>
    </section>
  )
}
